import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { formatMoney } from "@/lib/money";
import { Loader2, Check, X, Pause, CreditCard } from "lucide-react";

interface Row {
  id: string;
  user_id: string;
  cashtag: string;
  display_name: string;
  amount_cents: number;
  status: string;
  bank_name: string | null;
  bank_account_last4: string | null;
  recipient_name: string | null;
  notes: string | null;
  admin_notes: string | null;
  created_at: string;
}

const STATUSES = ["pending", "approved", "paid", "on_hold", "rejected"];

export default function AdminWithdrawals() {
  const [tab, setTab] = useState<string>("pending");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Row | null>(null);
  const [actionStatus, setActionStatus] = useState<string>("");
  const [adminNotes, setAdminNotes] = useState("");
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("admin_list_withdrawals", {
      _status: tab === "all" ? null : tab, _limit: 200,
    });
    setLoading(false);
    if (error) { toast({ title: "Load failed", description: error.message, variant: "destructive" }); return; }
    setRows((data ?? []) as Row[]);
  };

  useEffect(() => { load(); }, [tab]);

  const act = async (status: string) => {
    if (!selected) return;
    setActing(true);
    const { error } = await supabase.rpc("admin_set_withdrawal_status", {
      _withdrawal_id: selected.id, _status: status, _admin_notes: adminNotes || null,
    });
    setActing(false);
    if (error) { toast({ title: "Update failed", description: error.message, variant: "destructive" }); return; }
    toast({ title: `Marked ${status}` });
    setSelected(null); setAdminNotes(""); setActionStatus("");
    load();
  };

  return (
    <div className="p-6">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold">Withdrawals</h1>
        <p className="text-sm text-muted-foreground">Review, approve, hold, or reject user withdrawal requests.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          {STATUSES.map((s) => <TabsTrigger key={s} value={s}>{s}</TabsTrigger>)}
          <TabsTrigger value="all">all</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No withdrawals.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.id} className="p-3 flex items-center justify-between hover:bg-muted/40 cursor-pointer"
                  onClick={() => { setSelected(r); setAdminNotes(r.admin_notes ?? ""); }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{formatMoney(r.amount_cents)}</span>
                  <span className="text-sm">${r.cashtag}</span>
                  <Badge variant="outline">{r.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {r.recipient_name} · {r.bank_name} ••{r.bank_account_last4} · {new Date(r.created_at).toLocaleString()}
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Withdrawal · {selected && formatMoney(selected.amount_cents)}</DialogTitle>
            <DialogDescription>${selected?.cashtag} · {selected?.recipient_name}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-2 text-sm">
              <p><span className="text-muted-foreground">Bank:</span> {selected.bank_name} ••{selected.bank_account_last4}</p>
              <p><span className="text-muted-foreground">Status:</span> <Badge variant="outline">{selected.status}</Badge></p>
              <p><span className="text-muted-foreground">Requested:</span> {new Date(selected.created_at).toLocaleString()}</p>
              {selected.notes && <p><span className="text-muted-foreground">User notes:</span> {selected.notes}</p>}
              <div className="pt-3">
                <Label>Admin notes</Label>
                <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={3} />
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => act("on_hold")} disabled={acting}>
              <Pause className="h-4 w-4" /> Hold
            </Button>
            <Button variant="destructive" size="sm" onClick={() => act("rejected")} disabled={acting}>
              <X className="h-4 w-4" /> Reject (refund)
            </Button>
            <Button size="sm" onClick={() => act("approved")} disabled={acting}>
              <Check className="h-4 w-4" /> Approve
            </Button>
            <Button size="sm" variant="default" onClick={() => act("paid")} disabled={acting}>
              <CreditCard className="h-4 w-4" /> Mark paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
